"use client";

import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";

function SeoBentogridSkeleton({ isSmall }: { isSmall: boolean }) {
  const width = isSmall ? 410 : 880;
  const height = isSmall ? 880 : 360;

  return (
    <div
      className={cn(
        "relative w-full mx-auto mt-5 lg:mt-10 h-160px sm:h-[180px] md:h-[230px] overflow-hidden animate-pulse",
        isSmall ? "max-w-3xl" : "max-w-4xl"
      )}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      <div className="w-full h-full bg-neutral-200 dark:bg-neutral-800 rounded-md relative p-6 flex items-end justify-between"></div>
    </div>
  );
}

export function SeoBentogrid() {
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [hoverLength, setHoverLength] = useState<number>(400);
  const [isVertical, setIsVertical] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    const updateView = () => {
      const width = window.innerWidth;
      const isVerticalView = width >= 1024;
      const isSmall = width < 640;

      setIsVertical(isVerticalView);
      setIsSmallScreen(isSmall);

      // aspettiamo un tick per far aggiornare il nuovo path nel DOM
      requestAnimationFrame(() => {
        const path = pathRef.current;
        if (!path) return;

        const totalLength = path.getTotalLength();

        const desiredLength = isSmall
          ? totalLength * 0.43
          : totalLength * 0.35;
        const maxLength = totalLength - 95; // limite anche qui
        const clampedLength = Math.min(desiredLength, maxLength);

        setHoverLength(clampedLength);
        targetRef.current = clampedLength;
      });
    };

    updateView();
    window.addEventListener("resize", updateView);
    return () => window.removeEventListener("resize", updateView);
  }, []);

  // Movimento fluido: animiamo con lerp
  const targetRef = useRef<number>(400);

  useEffect(() => {
    const path = pathRef.current;
    if (path) {
      const centerLength = path.getTotalLength() / 2;
      setHoverLength(centerLength);
    }
  }, []);

  useEffect(() => {
    let animationFrame: number;

    const animate = () => {
      setHoverLength((prev) => {
        const next = prev + (targetRef.current - prev) * 0.1;
        return Math.abs(next - targetRef.current) < 0.5
          ? targetRef.current
          : next;
      });
      animationFrame = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  const pathDataHorizontal =
    "M0 312 L40 300 L80 280 L120 260 L160 240 L200 220 L240 200 L280 180 L320 160 L360 140 L400 120 L440 100 L480 80 L520 60 L560 50 L600 40 L640 38 L680 36 L720 34 L760 32 L800 30 L840 30 L880 32";

  const pathDataVertical =
    "M0 312 H50 L100 280 L150 240 L200 200 L250 160 L300 120 L350 100 L400 80 L450 60 L500 40 L550 30 L600 40 L650 60 L700 80 L750 100 L800 120 L840 150";

  const pathData = isVertical ? pathDataVertical : pathDataHorizontal;
  const viewBox = isVertical ? "0 -10 410 880" : "0 -10 880 360";
  const guideColor = isDark ? "#1E1E1E" : "#ECECEC";
  const verticalLineColor = isDark ? "#191919" : "#F2F2F2";
  const textColor = isDark ? "#666666" : "#A3A3A3";
  const circleColor = isDark ? "black" : "white";
  const circleStrokeColor = isDark ? "#3E3E3E" : "#E2E2E2";

  function handleMouseMove(e: React.MouseEvent<SVGSVGElement>) {
    const svg = svgRef.current;
    const path = pathRef.current;
    if (!svg || !path) return;

    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const cursor = pt.matrixTransform(svg.getScreenCTM()?.inverse());
    const totalLength = path.getTotalLength();

    let closestLength = 0;
    let minDistance = Infinity;
    for (let l = 0; l <= totalLength; l += 1) {
      const p = path.getPointAtLength(l);
      const dist = Math.abs(cursor.x - p.x);
      if (dist < minDistance) {
        minDistance = dist;
        closestLength = l;
      }
    }
    const maxLength = path.getTotalLength() - 95;
    targetRef.current = Math.min(closestLength, maxLength);
  }

  function getCircleProps(length: number) {
    const point = pathRef.current!.getPointAtLength(length);
    const clampedX = Math.max(30, Math.min(820, point.x)); // padding 30 ai bordi
    return { cx: clampedX, cy: point.y };
  }

  function getVisitorCount(length: number) {
    const y = getCircleProps(length).cy;
    const value = Math.round(((312 - y) / 312) * 2000);
    return value;
  }

  if (!hasMounted)
    return (
      <div className="h-full w-full p-4 overflow-hidden z-50">
        <h3 className="text-md sm:text-lg lg:text-2xl font-semibold">
          Built to be found. <br className="hidden lg:block" /> Optimized to
          perform.
        </h3>
        <div className="relative w-full max-w-4xl mx-auto mt-10">
          <SeoBentogridSkeleton isSmall={isSmallScreen} />
        </div>
      </div>
    );

  return (
    <div className="h-full w-full p-4 overflow-hidden z-50">
      <h3 className="text-md sm:text-lg lg:text-2xl font-semibold">
        Built to be found. <br className="hidden lg:block" /> Optimized to
        perform.
      </h3>
      <div className="relative w-full max-w-4xl mx-auto mt-5 lg:mt-10">
        <svg
          ref={svgRef}
          viewBox={viewBox}
          fill="none"
          onMouseMove={isSmallScreen ? undefined : handleMouseMove}
          className="w-[520px] sm:w-full h-[200px] sm:h-[230px] md:h-[260px] lg:h-auto"
        >
          {/* Gradient */}
          <defs>
            <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="60%" stopColor={isDark ? "#0094FF" : "#52AEFF"} stopOpacity="1" />
              <stop offset="30%" stopColor={isDark ? "#0094FF" : "#52AEFF"} stopOpacity="0.9" />
              <stop offset="97%" stopColor={isDark ? "#0094FF" : "#52AEFF"} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {[0, 500, 1000, 1500, 2000].map((val) => (
            <g key={val}>
              <text
                x="0"
                y={(1 - val / 2000) * 312}
                fill={textColor}
                fontSize="12"
                className="select-none"
              >
                {val}
              </text>
              <line
                x1="30"
                x2="880"
                y1={(1 - val / 2000) * 312}
                y2={(1 - val / 2000) * 312}
                stroke={guideColor}
                strokeWidth="2"
              />
            </g>
          ))}

          {/* Line path */}
          <path
            ref={pathRef}
            d={pathData}
            stroke="url(#gradient)"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />

          {/* Cursor elements (always visible) */}
          {pathRef.current && (
            <>
              <line
                x1={getCircleProps(hoverLength).cx}
                y1="3"
                x2={getCircleProps(hoverLength).cx}
                y2="309"
                stroke={verticalLineColor}
                strokeWidth="4"
                pointerEvents="none"
                strokeLinecap="round"
              />
              <circle
                r="8.5"
                fill={circleColor}
                stroke={circleStrokeColor}
                strokeWidth="1"
                pointerEvents="none"
                {...getCircleProps(hoverLength)}
              />
              <circle
                r="4.5"
                fill={isDark ? "#0094FF" : "#52AEFF"}
                pointerEvents="none"
                {...getCircleProps(hoverLength)}
              />
              {(() => {
                const { cx, cy } = getCircleProps(hoverLength);
                const boxWidth = 120;
                const padding = 20;
                const threshold = 1000;

                const currentValue = getVisitorCount(hoverLength);
                const toLeft = currentValue > threshold;
                const percentage = Math.round((currentValue / 2000) * 100);

                const tooltipX = toLeft
                  ? Math.max(padding, cx - boxWidth - 20)
                  : Math.min(cx + 10, 600 - boxWidth - padding);

                return (
                  <foreignObject
                    x={tooltipX}
                    y={cy - 20}
                    width={boxWidth}
                    height="60"
                  >
                    <div className="border dark:border-[#2A2A2A] border-[#E7E7E7] bg-white dark:bg-black text-md px-2 py-1 rounded-lg">
                      <h6 className="text-[#666666] dark:text-[#A1A1A1]">
                        Visitors
                      </h6>
                      {currentValue} &nbsp;{" "}
                      <span className="text-md text-[#0068D6] dark:text-[#52A8FF] bg-[#EAF5FF] dark:bg-[#0F233D] px-[5px] rounded-full">
                        +{percentage}%
                      </span>
                    </div>
                  </foreignObject>
                );
              })()}
            </>
          )}
        </svg>
      </div>
      {/* Shadow */}
      <div className="absolute bottom-30 lg:bottom-10 right-0 w-[19%] sm:w-30 md:w-64 lg:w-10 h-[220px] md:h-[300px] lg:h-[360px] bg-linear-to-l from-[#FAFAFA] via-[#FAFAFA] to-transparent dark:from-black dark:via-black pointer-events-auto z-30" />
    </div>
  );
}
